import dbStorageService from './dbStorageService';
// ASG services removed - medication notifications disabled
// import medicationService from '../_archive/asg/services/medicationService';
import { v4 as uuidv4 } from 'uuid';
import { NOTIFICATION_EVENTS } from './eventConstants';

// Import notification helpers
import {
  NotificationRegistry,
  CHECK_INTERVAL_MS,
  cleanupDuplicateNotifications,
  checkOverdueMedications,
  checkOverdueHealthChecks,
  handleMedicationTakenEvent,
  handleHealthCheckCompletedEvent,
  useNotificationService,
  createSyncHandlers,
  createCleanupHandlers,
  notificationStorage
} from './notificationHelpers';

/**
 * Notification service
 * Handles fetching, creating, and managing notifications
 */
class NotificationService {
  constructor() {
    this.listeners = new Set();
    this.notificationsCache = null;
    this.isChecking = false;
    this.lastCheck = null;
    this.checkIntervalId = null;
    this.notificationCheckingPaused = false;
    this.notificationRegistry = new NotificationRegistry();
    this.currentUserId = null;

    // Create event handlers
    this.syncHandlers = createSyncHandlers(this);
    this.cleanupHandlers = createCleanupHandlers(this);

    // Create bound event handler methods
    this._handleMedicationTakenEvent = this._handleMedicationTakenEvent.bind(this);
    this._handleHealthCheckCompletedEvent = this._handleHealthCheckCompletedEvent.bind(this);

    // Set up event listeners for sync events
    this.syncHandlers.setupSyncEventListeners();
  }

  /**
   * Pause notification checking
   */
  pauseNotificationChecking() {
    this.notificationCheckingPaused = true;
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
      // console.log('[NotificationService] Notification checking paused');
    }
  }

  /**
   * Resume notification checking
   */
  resumeNotificationChecking() {
    this.notificationCheckingPaused = false;

    if (this.currentUserId) {
      // Refresh the registry first to ensure it has all synced notifications
      this.notificationRegistry.initialize(this.currentUserId)
        .then(() => {
          // Run a check
          this.checkForOverdueItems(this.currentUserId);
          // Restart interval if not already running
          if (!this.checkIntervalId) {
            this.startPeriodicCheck(this.currentUserId);
          }
          // console.log('[NotificationService] Notification checking resumed');
        });
    }
  }

  /**
   * Initialize the notification service
   * @param {number} userId - The current user ID
   */
  async initialize(userId) {
    if (!userId) return;

    try {
      // Set current user ID
      // console.log('[NotificationService] Initializing with user ID:', userId);
      this.currentUserId = userId;
      await dbStorageService.initialize();

      // Make sure we're not paused
      this.notificationCheckingPaused = false;

      // Stop existing interval if any
      this.stopPeriodicCheck();

      // Wait for first sync to complete
      // console.log('[NotificationService] Waiting for first sync to complete before checking notifications');
      await this.waitForFirstSync(userId);

      // Initialize the notification registry
      await this.notificationRegistry.initialize(userId);

      // Do an initial check
      await this.checkForOverdueItems(userId);

      // Start periodic checking
      this.startPeriodicCheck(userId);

      // Subscribe to sync events to refresh notifications
      import('./syncService').then(({ default: syncService }) => {
        syncService.addSyncListener(() => this.refreshNotifications(userId));
      }).catch(err => console.warn('[NotificationService] failed to dynamically import syncService:', err));

      // Add event listeners for database cleanup
      this.cleanupHandlers.setupCleanupEventHandlers();

      // Add event listeners for medication taken and health check completed events
      this.setupActionEventHandlers();

      // console.log('[NotificationService] Initialization complete');
    } catch (error) {
      console.error('Failed to initialize notification service:', error);
    }
  }

  /**
   * Wait for first sync to complete
   * @param {number} userId - The current user ID
   * @returns {Promise<void>} A promise that resolves when the first sync completes or times out
   */
  async waitForFirstSync(userId) {
    return new Promise((resolve) => {
      let resolved = false;

      // Function to call when sync completes
      const onSyncComplete = (event) => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener('sync:sync_completed', onSyncComplete);
        window.removeEventListener('sync:sync_failed', onSyncComplete);
        // console.log('[NotificationService] First sync completed, continuing initialization');

        // Ensure we're not paused
        this.notificationCheckingPaused = false;

        resolve();
      };

      // Listen for sync completion or failure
      window.addEventListener('sync:sync_completed', onSyncComplete);
      window.addEventListener('sync:sync_failed', onSyncComplete);

      // Set a timeout in case sync never completes
      setTimeout(() => {
        if (resolved) return;
        resolved = true;
        window.removeEventListener('sync:sync_completed', onSyncComplete);
        window.removeEventListener('sync:sync_failed', onSyncComplete);
        // console.log('[NotificationService] Sync wait timed out, continuing initialization');

        // Ensure we're not paused
        this.notificationCheckingPaused = false;

        resolve();
      }, 5000); // 5 second timeout
    });
  }

  /**
   * Start periodic checking for overdue items
   * @param {number} userId - The current user ID
   */
  startPeriodicCheck(userId) {
    // Clear any existing interval
    this.stopPeriodicCheck();

    // Set up new interval
    this.checkIntervalId = setInterval(() => {
      this.checkForOverdueItems(userId);
    }, CHECK_INTERVAL_MS);

    console.log(`Notification check interval started for user ${userId}`);
  }

  /**
   * Stop periodic checking
   */
  stopPeriodicCheck() {
    if (this.checkIntervalId) {
      clearInterval(this.checkIntervalId);
      this.checkIntervalId = null;
      // console.log('Notification check interval stopped');
    }
  }

  /**
   * Check for overdue medications and health checks
   * @param {number} userId - The current user ID
   */
  async checkForOverdueItems(userId) {
    if (this.isChecking || !userId || this.notificationCheckingPaused) {
      // console.log('[NotificationService] Skipping check - isChecking:', this.isChecking,
      //   'paused:', this.notificationCheckingPaused,
      //   'userId:', !!userId);

      // If paused for too long, auto-resume
      if (this.notificationCheckingPaused && this.currentUserId) {
        const lastResumeAttempt = this._lastResumeAttempt || 0;
        const now = Date.now();

        // Try to auto-resume if it's been more than a minute
        if (now - lastResumeAttempt > 60000) {
          // console.log('[NotificationService] Auto-resuming notification checking after pause');
          this._lastResumeAttempt = now;
          this.resumeNotificationChecking();
        }
      }

      return;
    }

    try {
      this.isChecking = true;

      // Get all current notifications to avoid creating duplicates
      const existingNotifications = await this.getNotifications(userId);

      // Perform a cleanup of duplicate notifications to fix existing duplicates
      await cleanupDuplicateNotifications(existingNotifications);

      // ASG features removed - medication and health check notifications disabled
      // const medicationNotifications = await checkOverdueMedications(userId, this.notificationRegistry);
      // const healthCheckNotifications = await checkOverdueHealthChecks(userId, this.notificationRegistry);
      const medicationNotifications = [];
      const healthCheckNotifications = [];

      // Save all new notifications
      const newNotifications = [...medicationNotifications, ...healthCheckNotifications];
      if (newNotifications.length > 0) {
        await this.saveNotifications(newNotifications);
        // console.log(`Created ${newNotifications.length} total notifications`);
      }

      this.lastCheck = new Date();
    } catch (error) {
      console.error('Error checking for overdue items:', error);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Save notifications to the database
   * @param {Array} notifications - Notifications to save
   */
  async saveNotifications(notifications) {
    try {
      if (!notifications || notifications.length === 0) return;

      // Save to database
      await notificationStorage.saveNotifications(notifications);

      // Update registry with new/updated notifications
      for (const notification of notifications) {
        this.notificationRegistry.addNotification(notification);
      }

      // Invalidate cache
      this.notificationsCache = null;

      // Notify listeners
      this.notifyListeners();
    } catch (error) {
      console.error('Error saving notifications:', error);
      throw error;
    }
  }

  /**
   * Get all notifications for a user and users in their circle of trust
   * @param {number} userId - The user ID
   * @returns {Array} List of notifications
   */
  async getNotifications(userId) {
    try {
      // Return cached notifications if available
      if (this.notificationsCache) {
        return this.notificationsCache;
      }

      // Fetch notifications using the storage module
      const activeNotifications = await notificationStorage.getNotifications(userId);

      // Cache the result
      this.notificationsCache = activeNotifications;

      return activeNotifications;
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  /**
   * Get unread notifications count
   * @param {number} userId - The user ID
   * @returns {Promise<number>} Count of unread notifications
   */
  async getUnreadCount(userId) {
    try {
      const notifications = await this.getNotifications(userId);
      return notifications.filter(n => !n.isRead).length;
    } catch (error) {
      console.error('Error getting unread count:', error);
      return 0;
    }
  }

  /**
   * Mark a notification as read
   * @param {string} uuid - The notification UUID
   * @param {boolean} isRead - Whether to mark as read or unread
   */
  async markAsRead(uuid, isRead = true) {
    try {
      // Use the storage module to mark as read
      const updatedNotification = await notificationStorage.markAsRead(uuid, isRead);

      if (!updatedNotification) {
        return false;
      }

      // Update in registry
      this.notificationRegistry.addNotification(updatedNotification);

      // Invalidate cache
      this.notificationsCache = null;

      // Notify listeners
      this.notifyListeners();

      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  /**
   * Mark a notification for deletion
   * @param {string} uuid - The notification UUID
   * @returns {Promise<boolean>} Whether the operation was successful
   */
  async deleteNotification(uuid) {
    try {
      // Use the storage module to delete
      const deletedNotification = await notificationStorage.deleteNotification(uuid);

      if (!deletedNotification) {
        return false;
      }

      // Mark as deleted in registry to prevent immediate recreation
      this.notificationRegistry.markAsDeleted(uuid);

      // Clear cache and notify listeners
      this.notificationsCache = null;
      this.notifyListeners();

      return true;
    } catch (error) {
      console.error('Error deleting notification:', error);
      return false;
    }
  }

  /**
   * Record that a medication was taken
   * @param {string} notificationUuid - The notification UUID
   * @param {string} userConfigUuid - The medication UserConfig UUID
   * @param {number} userId - The user ID
   */
  async recordMedicationTaken(notificationUuid, userConfigUuid, userId) {
    try {
      // ASG features removed - medication tracking disabled
      console.warn('[NotificationService] recordMedicationTaken called but ASG features are disabled');

      // Mark the notification as read
      await this.markAsRead(notificationUuid, true);

      return true;
    } catch (error) {
      console.error('Error recording medication taken:', error);
      return false;
    }
  }

  /**
   * Refresh notifications cache
   * @param {number} userId - The user ID
   */
  async refreshNotifications(userId) {
    // Invalidate cache to force a refresh
    this.notificationsCache = null;

    // Refresh notification registry
    await this.notificationRegistry.initialize(userId);

    // Ensure notification checking is resumed
    this.resumeNotificationChecking();

    // Notify listeners
    this.notifyListeners();

    // Check if we should run a check for overdue items
    if (!this.notificationCheckingPaused) {
      await this.checkForOverdueItems(userId);
    }
  }

  /**
   * Add a listener to be notified of changes
   * @param {Function} listener - The listener function
   */
  addListener(listener) {
    this.listeners.add(listener);
  }

  /**
   * Remove a listener
   * @param {Function} listener - The listener function to remove
   */
  removeListener(listener) {
    this.listeners.delete(listener);
  }

  /**
   * Notify all listeners of changes
   */
  notifyListeners() {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  /**
   * Set up event handlers for medication and health check actions
   */
  setupActionEventHandlers() {
    // Listen for medication taken events (new format)
    window.addEventListener(NOTIFICATION_EVENTS.MEDICATION_TAKEN, this._handleMedicationTakenEvent);

    // Listen for health check completed events (new format)
    window.addEventListener(NOTIFICATION_EVENTS.HEALTH_CHECK_COMPLETED, this._handleHealthCheckCompletedEvent);

    // console.log('[NotificationService] Set up action event handlers');
  }

  /**
   * Internal wrapper for medication taken event handler
   * @param {CustomEvent} event - The event with medication details
   */
  async _handleMedicationTakenEvent(event) {
    await handleMedicationTakenEvent(
      event,
      this.getNotifications.bind(this),
      this.markAsRead.bind(this),
      this.deleteNotification.bind(this)
    );
  }

  /**
   * Internal wrapper for health check completed event handler
   * @param {CustomEvent} event - The event with health check details
   */
  async _handleHealthCheckCompletedEvent(event) {
    await handleHealthCheckCompletedEvent(
      event,
      this.getNotifications.bind(this),
      this.markAsRead.bind(this),
      this.deleteNotification.bind(this)
    );
  }

  /**
   * Clean up the service
   */
  cleanup() {
    this.stopPeriodicCheck();
    this.listeners.clear();
    this.notificationsCache = null;

    // Reset notification registry
    this.notificationRegistry.reset();
    this.currentUserId = null;

    // Remove cleanup event handlers
    this.cleanupHandlers.removeCleanupEventHandlers();

    // Remove sync event listeners
    this.syncHandlers.removeSyncEventListeners();

    // Remove action event listeners (both new and legacy formats)
    window.removeEventListener(NOTIFICATION_EVENTS.MEDICATION_TAKEN, this._handleMedicationTakenEvent);
    window.removeEventListener(NOTIFICATION_EVENTS.HEALTH_CHECK_COMPLETED, this._handleHealthCheckCompletedEvent);

    // console.log('[NotificationService] Cleanup complete');
  }
}

// Create and export a singleton instance
const notificationService = new NotificationService();
export default notificationService;

// Export the hook for ease of use
export { useNotificationService };

